import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from '../../context/SessionContext';
import { useNavigation } from '../../hooks/useNavigation';
import { StickyMenu } from '../ui';
import { VIDEO_ENDPOINTS } from '../../constants/apiEndpoints';
import { useReactMediaRecorder } from 'react-media-recorder';
import toast from 'react-hot-toast';

// Live video component that manages its own video element
const LiveVideoFeed = React.forwardRef(({ previewStream }, ref) => {
  const videoRef = useRef(null);
  
  useEffect(() => {
    if (videoRef.current && previewStream) {
      // Only set srcObject if it's different
      if (videoRef.current.srcObject !== previewStream) {
        videoRef.current.srcObject = previewStream;
      }
    }
  }, [previewStream]);

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  React.useImperativeHandle(ref, () => ({
    getVideoElement: () => videoRef.current
  }));

  return (
    <video 
      ref={videoRef}
      autoPlay 
      playsInline 
      muted 
      className="w-full h-full object-cover"
      style={{ transform: 'scaleX(-1)' }}
    />
  );
});

// Timer display component - completely isolated
const TimerDisplay = ({ isRecording, maxTime }) => {
  const [time, setTime] = useState(0);
  
  useEffect(() => {
    if (!isRecording) {
      setTime(0);
      return;
    }
    
    const interval = setInterval(() => {
      setTime(t => t + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isRecording]);
  
  if (!isRecording) return null;
  
  const remainingTime = maxTime - time;
  const minutes = Math.floor(remainingTime / 60);
  const seconds = remainingTime % 60;
  
  return (
    <div className="absolute top-4 right-4 flex items-center bg-red-500 text-white px-3 py-1 rounded-full pointer-events-none">
      <div className="w-3 h-3 bg-white rounded-full mr-2 animate-pulse"></div>
      <span>Recording {minutes}:{String(seconds).padStart(2, '0')}</span>
    </div>
  );
};


function VideoRecorderComponent({ onReset, onVideoRecorded, isUploading, uploadSuccess, onSubmitVideo }) {
  const maxRecordingTime = 90; // 90 seconds limit
  const liveVideoRef = useRef(null);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  
  // Countdown state
  const [countdown, setCountdown] = useState(0);
  const [isCountingDown, setIsCountingDown] = useState(false);
  
  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    previewStream
  } = useReactMediaRecorder({
    video: { facingMode: 'user' },
    audio: true,
    askPermissionOnMount: true,
    blobPropertyBag: { type: 'video/webm' },
    mediaRecorderOptions: {
      audioBitsPerSecond: 128000,
      videoBitsPerSecond: 2500000,
      mimeType: 'video/webm'
    }
  });

  // Handle countdown and start recording
  const handleStartRecording = () => {
    setIsCountingDown(true);
    setCountdown(5);
  };

  // Handle countdown with useEffect
  useEffect(() => {
    if (!isCountingDown || countdown <= 0) return;

    const timer = setTimeout(() => {
      if (countdown === 1) {
        setIsCountingDown(false);
        setCountdown(0);
        startRecording();
      } else {
        setCountdown(countdown - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [isCountingDown, countdown, startRecording]);

  // Auto-stop recording after max time
  useEffect(() => {
    if (status === 'recording') {
      timerRef.current = setTimeout(() => {
        stopRecording();
      }, maxRecordingTime * 1000);
    } else {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [status, stopRecording, maxRecordingTime]);

  // Cleanup media stream when component unmounts
  useEffect(() => {
    return () => {
      if (previewStream) {
        const tracks = previewStream.getTracks();
        tracks.forEach(track => {
          track.stop();
          console.log('Stopped media track:', track.kind);
        });
      }
      // Clear countdown timer
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [previewStream]);

  // Notify parent when video is recorded
  React.useEffect(() => {
    if (mediaBlobUrl) {
      // Convert blob URL to actual blob for upload
      fetch(mediaBlobUrl)
        .then(response => response.blob())
        .then(blob => {
          onVideoRecorded({
            blob: blob,
            url: mediaBlobUrl,
            mimeType: 'video/webm'
          });
        })
        .catch(error => {
          console.error('Error converting blob URL to blob:', error);
        });
    }
  }, [mediaBlobUrl, onVideoRecorded]);

  return (
    <>
      {/* Video Display */}
      <div className="w-full mb-6">
        <div className="relative">
          {mediaBlobUrl ? (
            <video 
              src={mediaBlobUrl}
              controls 
              autoPlay
              playsInline
              className="w-full rounded-lg object-cover"
              style={{ height: '360px' }}
            />
          ) : (
            <div className="w-full rounded-lg bg-black overflow-hidden relative" style={{ height: '360px' }}>
              <LiveVideoFeed ref={liveVideoRef} previewStream={previewStream} />
              {/* Game-like Countdown Animation - Only over video */}
              {isCountingDown && countdown > 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg z-10">
                  <div 
                    key={countdown}
                    className="text-white font-black drop-shadow-2xl"
                    style={{
                      fontSize: '8rem',
                      textShadow: '0 0 50px rgba(255,255,255,0.8), 0 0 100px rgba(255,255,255,0.6)',
                      animation: 'flyToUser 1s ease-out forwards'
                    }}
                  >
                    {countdown}
                  </div>
                </div>
              )}
            </div>
          )}
          <TimerDisplay isRecording={status === 'recording'} maxTime={maxRecordingTime} />
        </div>
      </div>
      
      {/* Control Buttons */}
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {!mediaBlobUrl ? (
          status !== 'recording' && !isCountingDown ? (
            <button 
              onClick={handleStartRecording}
              disabled={status === 'acquiring_media'}
              className={`px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-medium rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors duration-200 shadow-md flex items-center ${
                status === 'acquiring_media' ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <circle cx="12" cy="12" r="4" fill="currentColor" />
              </svg>
              {status === 'acquiring_media' ? 'Getting Camera...' : 'Start Recording'}
            </button>
          ) : isCountingDown ? (
            <button 
              disabled
              className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-medium rounded-lg opacity-75 cursor-not-allowed shadow-md flex items-center"
            >
              <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <circle cx="12" cy="12" r="4" fill="currentColor" />
              </svg>
              Starting in {countdown}...
            </button>
          ) : (
            <button 
              onClick={stopRecording} 
              className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg hover:from-red-600 hover:to-red-700 transition-colors duration-200 shadow-md flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="6" width="12" height="12" strokeWidth="2" />
              </svg>
              Stop Recording
            </button>
          )
        ) : (
          <>
            <button 
              onClick={onReset} 
              disabled={isUploading}
              className={`px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-medium rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors duration-200 shadow-md flex items-center ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Record Again
            </button>
            <button 
              onClick={onSubmitVideo}
              disabled={isUploading || uploadSuccess}
              className={`px-6 py-3 text-white font-medium rounded-lg transition-colors duration-200 shadow-md flex items-center ${(isUploading || uploadSuccess) ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ backgroundColor: '#33C6B6' }}
              onMouseEnter={(e) => !isUploading && !uploadSuccess && (e.currentTarget.style.backgroundColor = '#2BA89F')}
              onMouseLeave={(e) => !isUploading && !uploadSuccess && (e.currentTarget.style.backgroundColor = '#33C6B6')}
            >
              {isUploading ? (
                <>
                  <div className="w-5 h-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {uploadSuccess ? 'Submitted' : 'Submit Video'}
                </>
              )}
            </button>
          </>
        )}
      </div>
    </>
  );
}

function VideoIntro() {
  const { sessionData, eid, updateSessionData } = useSession();
  const { navigateNext, navigatePrevious } = useNavigation();
  const location = useLocation();
  
  // State variables
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [recorderKey, setRecorderKey] = useState(0);

  // Cleanup when component unmounts or page changes
  useEffect(() => {
    return () => {
      // Force cleanup of any active media streams
      navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        .then(stream => {
          stream.getTracks().forEach(track => track.stop());
        })
        .catch(() => {
          // Ignore errors - this is just cleanup
        });
    };
  }, []);

  // Handle video recorded from child component
  const handleVideoRecorded = (videoData) => {
    setRecordedVideo(videoData);
    setError(null); // Clear any previous errors
  };

  // Reset recording
  const handleReset = () => {
    console.log("Resetting recording...");
    
    // Release any object URLs to prevent memory leaks
    if (recordedVideo && recordedVideo.url) {
      URL.revokeObjectURL(recordedVideo.url);
      console.log("Revoked object URL");
    }
    
    // Clear the recorded video state
    setRecordedVideo(null);
    
    // Clear any error messages
    setError(null);
    
    // Reset upload states
    setIsUploading(false);
    setUploadSuccess(false);
    
    // Force complete remount of the recorder component
    setRecorderKey(prev => prev + 1);
    
    console.log("Recording reset complete");
  };
  
  // Submit video to webhook
  const submitVideo = async () => {
    if (!recordedVideo || !recordedVideo.blob) {
      setError("No video to submit");
      return;
    }
    
    setIsUploading(true);
    setError(null);
    
    try {
      console.log("Preparing to upload video...");
      
      // Create FormData to send the video
      const formData = new FormData();
      formData.append('video', recordedVideo.blob, 'video_introduction.webm');
      formData.append('eid', eid);
      
      console.log("Uploading video to webhook...");
      
      // Send the video to the proper endpoint
      const response = await fetch(VIDEO_ENDPOINTS.SAVE_VIDEO, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }
      
      console.log("Video uploaded successfully");
      setUploadSuccess(true);
      
      // Show success toast
      toast((t) => (
        <div className="flex items-center justify-between">
          <span className="text-gray-900 font-medium">Video saved successfully</span>
          <button 
            onClick={() => toast.dismiss(t.id)}
            className="ml-4 text-gray-500 hover:text-gray-700 font-medium"
          >
            Dismiss
          </button>
        </div>
      ), {
        duration: 5000,
        position: 'top-center',
      });
      
      // Update session data to mark video as received
      const updateSuccess = updateSessionData({ intro_video_received: true });
      
    } catch (err) {
      console.error("Error uploading video:", err);
      setError(`Error uploading video: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Navigation handlers
  const handleNext = () => {
    if (sessionData?.intro_video_received || uploadSuccess) {
      navigateNext(location.pathname);
    } else {
      setError("Please record and submit your video introduction first.");
    }
  };

  const handlePrevious = () => {
    navigatePrevious(location.pathname);
  };
  
  return (
    <>
      <style jsx>{`
        @keyframes flyToUser {
          0% {
            transform: scale(0.1) translateZ(-1000px);
            opacity: 0;
          }
          30% {
            transform: scale(0.8) translateZ(-200px);
            opacity: 1;
          }
          60% {
            transform: scale(1.2) translateZ(0px);
            opacity: 1;
          }
          100% {
            transform: scale(3) translateZ(500px);
            opacity: 0;
          }
        }
      `}</style>
      
      <div className="flex flex-col min-h-screen p-0 pt-20 pb-16 w-full">
        <StickyMenu />
      
        {/* 2-Column Layout Container */}
        <div className="flex-1 flex justify-center px-4">
          <div className="w-full max-w-[1100px] bg-white rounded-3xl shadow-md flex">
            {/* Left Column - Main Content */}
            <div className="flex-1 py-12 px-8 flex flex-col items-center border-r border-gray-200">
              <h2 className="text-3xl font-semibold mb-2 text-center">Video Introduction</h2>
              <p className="text-lg text-gray-600 mb-6 text-center px-12">
                Please introduce yourself over a short video and tell us why you're interested in this position <b>(90 seconds max)</b>.
              </p>
        
        {error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6 w-full max-w-xl">
            <p className="font-medium">Error</p>
            <p>{error}</p>
          </div>
        ) : null}
        
        
        {sessionData?.intro_video_received && !uploadSuccess ? (
          <div className="px-4 py-3 rounded-md mb-6 w-full max-w-2xl" style={{ backgroundColor: '#E6F9F7', borderWidth: '1px', borderColor: '#33C6B6' }}>
            <p className="font-medium" style={{ color: '#0B7A70' }}>Video already submitted.</p>
            <p style={{ color: '#1A9D92' }}>You can record a new one to replace it, or continue to the next step.</p>
          </div>
        ) : null}
        
        {/* Video Recorder Container */}
        <div className="w-full max-w-2xl mb-8">
          <VideoRecorderComponent 
            key={recorderKey} 
            onReset={handleReset} 
            onVideoRecorded={handleVideoRecorded}
            onSubmitVideo={submitVideo}
            isUploading={isUploading}
            uploadSuccess={uploadSuccess}
          />
          {/* Separator border */}
          <div className="border-b border-gray-200 mt-8"></div>
        </div>

              {/* Navigation buttons */}
              <div className="flex justify-between w-full max-w-2xl mt-8">
                <button
                  onClick={handlePrevious}
                  className="bg-white text-gray-700 border-2 border-gray-600 rounded-md px-6 py-4 text-xl flex items-center hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                
                <button
                  onClick={handleNext}
                  disabled={!uploadSuccess && !sessionData?.intro_video_received}
                  className={`text-white rounded-md px-6 py-4 text-xl ${
                    (!uploadSuccess && !sessionData?.intro_video_received) 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  Continue
                </button>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div 
              className="w-80 py-10 px-8 rounded-r-3xl flex flex-col justify-between relative" 
              style={{ 
                backgroundColor: '#F9F9F9',
                backgroundImage: 'url(/leftbg.svg), url(/hotcircle.svg)',
                backgroundSize: '120% auto, auto',
                backgroundRepeat: 'no-repeat, no-repeat',
                backgroundPosition: 'top center, bottom center'
              }}
            >
              <div>
                <h3 className="text-2xl font-regular text-gray-900 mb-6 leading-tight">
                  Introduction Tips
                </h3>

                <div className="space-y-4">
                  {/* Environment setup */}
                  <div className="py-2">
                    <div className="flex items-start">
                      <span className="flex-shrink-0 w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3"></span>
                      <div>
                        <p className="text-sm text-gray-900">
                          <span className="font-semibold">Setup:</span> Choose a quiet space with good lighting facing you. Make sure your face is clearly visible.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Delivery */}
                  <div className="py-2">
                    <div className="flex items-start">
                      <span className="flex-shrink-0 w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3"></span>
                      <div>
                        <p className="text-sm text-gray-900">
                          <span className="font-semibold">Delivery:</span> Speak naturally and look at the camera as if you're having a conversation with the hiring manager.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="py-2">
                    <div className="flex items-start">
                      <span className="flex-shrink-0 w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3"></span>
                      <div>
                        <p className="text-sm text-gray-900">
                          <span className="font-semibold">Content:</span> Share your relevant experience, key skills, and what excites you about this opportunity.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-300 pt-4">
                <div className="text-xs text-gray-500 text-center">
                  Powered by tendent.ai
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default VideoIntro;
